// ============================================================================
// Wave 3.99r — Cutter Ledger page.
//
// Per-cutter running balance derived from the broken-packet-issues
// collection. Answers the question every stone buyer asks daily:
// "how many carats does <cutter> still hold, and for how long?".
//
// UX notes:
//   • Material filter only surfaces materials the operator can see.
//     Karigar / SA / CA see all three stone materials; Diamond Buyer
//     sees just Diamonds; CS+Pearl sees Colour Stones + Pearls.
//   • Cutters currently holding stock sit at the top (sorted by
//     outstanding carats desc). Cutters with zero balance stay
//     visible so the buyer knows they're on the register.
//   • Each row shows outstanding + open-memo-count + last-activity
//     + per-material breakdown so buyers can spot stagnant
//     relationships without opening the memo list.
// ============================================================================
import React, { useEffect, useMemo, useState } from 'react';
import AppShell from '@/components/AppShell';
import { Card } from '@/components/ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Scissors, Timer, RefreshCw, ArrowRight } from 'lucide-react';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Link } from 'react-router-dom';

const MATERIAL_LABEL = {
  diamond: 'Diamonds',
  color_stone: 'Color Stones',
  pearl: 'Pearls',
};

const MATERIAL_ACCENT = {
  diamond: 'border-l-sky-400',
  color_stone: 'border-l-emerald-400',
  pearl: 'border-l-violet-400',
};

function formatCt(v) {
  const n = Number(v || 0);
  return `${n.toFixed(3)} ct`;
}

function formatRelative(iso) {
  if (!iso) return '—';
  try {
    const then = new Date(iso);
    const now = new Date();
    const diffMs = now - then;
    const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (days <= 0) return 'Today';
    if (days === 1) return '1 day ago';
    if (days < 30) return `${days} days ago`;
    const months = Math.floor(days / 30);
    return months === 1 ? '1 month ago' : `${months} months ago`;
  } catch {
    return '—';
  }
}

function activityBadgeClass(iso) {
  if (!iso) return 'bg-slate-100 text-slate-500 border border-slate-200';
  try {
    const days = Math.floor((new Date() - new Date(iso)) / (1000 * 60 * 60 * 24));
    if (days >= 30) return 'bg-red-50 text-red-700 border border-red-100';
    if (days >= 14) return 'bg-amber-50 text-amber-700 border border-amber-100';
    return 'bg-slate-100 text-slate-600 border border-slate-200';
  } catch {
    return 'bg-slate-100 text-slate-500 border border-slate-200';
  }
}

export default function CutterLedger() {
  return (
    <AppShell title="Cutter Ledger" subtitle="Running balance for every cutter — outstanding carats, open memos, and last activity.">
      <CutterLedgerContent />
    </AppShell>
  );
}

function CutterLedgerContent() {
  const { effectiveCompanyId, hasMaterialAccess } = useAuth();
  const availableMaterials = useMemo(() => {
    const mats = [];
    if (hasMaterialAccess('diamond', 'see')) mats.push({ value: 'diamond', label: 'Diamonds' });
    if (hasMaterialAccess('color_stone', 'see')) mats.push({ value: 'color_stone', label: 'Color Stones' });
    if (hasMaterialAccess('pearl', 'see')) mats.push({ value: 'pearl', label: 'Pearls' });
    return mats;
  }, [hasMaterialAccess]);

  const [materialFilter, setMaterialFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ cutters: [], totals: {}, material_scope: [] });
  const [refreshTs, setRefreshTs] = useState(0);

  useEffect(() => {
    if (availableMaterials.length === 1 && materialFilter === 'all') {
      setMaterialFilter(availableMaterials[0].value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableMaterials.length]);

  useEffect(() => {
    if (!effectiveCompanyId) return;
    let cancelled = false;
    async function fetchLedger() {
      setLoading(true);
      try {
        const params = { company_id: effectiveCompanyId };
        if (materialFilter && materialFilter !== 'all') params.material_kind = materialFilter;
        const { data: payload } = await api.get('/masters/cutters/ledger', { params });
        if (!cancelled) setData(payload || { cutters: [], totals: {}, material_scope: [] });
      } catch {
        if (!cancelled) setData({ cutters: [], totals: {}, material_scope: [] });
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    fetchLedger();
    return () => { cancelled = true; };
  }, [effectiveCompanyId, materialFilter, refreshTs]);

  const totals = data.totals || {};
  const activeCutters = data.cutters.filter((c) => c.outstanding_carats > 0 || c.open_issue_count > 0);
  const idleCutters = data.cutters.filter((c) => c.outstanding_carats === 0 && c.open_issue_count === 0);

  return (
    <div className="space-y-4">
      {/* Header row: material filter + refresh + totals */}
      <Card className="p-4">
        <div className="flex flex-wrap items-center gap-3 justify-between">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="w-48">
              <Select value={materialFilter} onValueChange={setMaterialFilter}>
                <SelectTrigger data-testid="cutter-ledger-material-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {availableMaterials.length > 1 && (
                    <SelectItem value="all">All materials</SelectItem>
                  )}
                  {availableMaterials.map((m) => (
                    <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setRefreshTs(Date.now())}
              data-testid="cutter-ledger-refresh"
            >
              <RefreshCw size={14} className="mr-1" /> Refresh
            </Button>
          </div>

          <div className="flex flex-wrap items-center gap-2 text-xs">
            <Badge className="bg-amber-50 text-amber-800 border border-amber-100" data-testid="cutter-ledger-total-outstanding">
              Outstanding: {formatCt(totals.outstanding_carats)} · {totals.outstanding_pieces || 0} pc
            </Badge>
            <Badge className="bg-slate-100 text-slate-700 border border-slate-200">
              Issued: {formatCt(totals.total_issued_carats)}
            </Badge>
            <Badge className="bg-emerald-50 text-emerald-800 border border-emerald-100">
              Returned: {formatCt(totals.total_returned_carats)}
            </Badge>
            <Badge className="bg-sky-50 text-sky-800 border border-sky-100">
              {totals.open_issue_count || 0} open memo{(totals.open_issue_count || 0) === 1 ? '' : 's'}
            </Badge>
          </div>
        </div>
      </Card>

      {loading ? (
        <Card className="p-8 text-center text-sm text-slate-500">Loading ledger…</Card>
      ) : (data.cutters.length === 0) ? (
        <Card className="p-8 text-center text-sm text-slate-500 flex flex-col items-center gap-2">
          <Scissors size={24} className="text-slate-300" />
          <span>No cutters on file yet. Add cutters from the Cutters master to start tracking balances.</span>
        </Card>
      ) : (
        <>
          {/* Active cutters — outstanding balance or open memos */}
          {activeCutters.length > 0 && (
            <Card className="p-0 overflow-hidden">
              <div className="px-4 py-2.5 border-b border-[var(--divider)] bg-[var(--paper-warm)]">
                <div className="text-xs font-medium text-slate-700 uppercase tracking-wide">
                  Holding stock ({activeCutters.length})
                </div>
              </div>
              <Table data-testid="cutter-ledger-active-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Cutter</TableHead>
                    <TableHead className="text-right">Outstanding</TableHead>
                    <TableHead className="text-right">Open Memos</TableHead>
                    <TableHead className="text-right">Issued</TableHead>
                    <TableHead className="text-right">Returned</TableHead>
                    <TableHead>Last Activity</TableHead>
                    <TableHead className="w-14"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {activeCutters.map((c) => (
                    <TableRow
                      key={c.cutter_id}
                      data-testid={`cutter-ledger-row-${c.cutter_id}`}
                    >
                      <TableCell>
                        <div className="font-medium text-[var(--ink-navy)]">{c.cutter_name}</div>
                        {c.specialization && (
                          <div className="text-[11px] text-slate-500">{c.specialization}</div>
                        )}
                        {/* Per-material breakdown chips */}
                        {c.by_material && Object.entries(c.by_material).length > 0 && (
                          <div className="mt-1 flex flex-wrap gap-1">
                            {Object.entries(c.by_material).map(([mk, ct]) => (
                              <span
                                key={mk}
                                className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-md border ${MATERIAL_ACCENT[mk] || 'border-l-slate-300'} border-slate-100 bg-white`}
                              >
                                <span className="text-slate-500">{MATERIAL_LABEL[mk] || mk}:</span>
                                <span className="font-mono tabular-nums text-slate-700">{Number(ct).toFixed(3)} ct</span>
                              </span>
                            ))}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-right font-mono tabular-nums">
                        <div className="text-amber-800 font-semibold">
                          {formatCt(c.outstanding_carats)}
                        </div>
                        <div className="text-[11px] text-slate-500">{c.outstanding_pieces} pc</div>
                      </TableCell>
                      <TableCell className="text-right">
                        <Badge className="bg-sky-50 text-sky-800 border border-sky-100">
                          {c.open_issue_count}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right font-mono tabular-nums text-slate-600">
                        {formatCt(c.total_issued_carats)}
                      </TableCell>
                      <TableCell className="text-right font-mono tabular-nums text-slate-600">
                        {formatCt(c.total_returned_carats)}
                      </TableCell>
                      <TableCell>
                        <span
                          className={`inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded-full ${activityBadgeClass(c.last_activity_iso)}`}
                          data-testid={`cutter-ledger-activity-${c.cutter_id}`}
                        >
                          <Timer size={10} /> {formatRelative(c.last_activity_iso)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Link
                          to={`/broken-packet-issues?recipient_type=cutter&recipient_id=${c.cutter_id}`}
                          className="text-sky-700 hover:text-sky-900 inline-flex items-center gap-1 text-xs"
                          data-testid={`cutter-ledger-open-issues-${c.cutter_id}`}
                          title="Open recut issues for this cutter"
                        >
                          Memos <ArrowRight size={12} />
                        </Link>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          )}

          {/* Idle cutters — zero outstanding + no open memos */}
          {idleCutters.length > 0 && (
            <Card className="p-0 overflow-hidden">
              <div className="px-4 py-2.5 border-b border-[var(--divider)] bg-[var(--paper-warm)]">
                <div className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                  Idle / no activity ({idleCutters.length})
                </div>
              </div>
              <Table data-testid="cutter-ledger-idle-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Cutter</TableHead>
                    <TableHead className="text-right">Lifetime Issued</TableHead>
                    <TableHead className="text-right">Lifetime Returned</TableHead>
                    <TableHead>Last Activity</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {idleCutters.map((c) => (
                    <TableRow key={c.cutter_id} data-testid={`cutter-ledger-idle-row-${c.cutter_id}`}>
                      <TableCell>
                        <div className="font-medium text-slate-700">{c.cutter_name}</div>
                        {c.specialization && (
                          <div className="text-[11px] text-slate-500">{c.specialization}</div>
                        )}
                      </TableCell>
                      <TableCell className="text-right font-mono tabular-nums text-slate-600">
                        {formatCt(c.total_issued_carats)}
                      </TableCell>
                      <TableCell className="text-right font-mono tabular-nums text-slate-600">
                        {formatCt(c.total_returned_carats)}
                      </TableCell>
                      <TableCell>
                        <span className="text-[11px] text-slate-500">{formatRelative(c.last_activity_iso)}</span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
